import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import type { TabsProps, TabProps } from './Tabs.types'
import './tabs.scss'

const Tabs = ({ tabsList, center, small, color, onChange }: TabsProps) => {
  const isCenter = center ? 'sb-tabs--center' : 'sb-tabs--left'
  const isSmall = small ? 'sb-tabs--small' : ''

  const [widthWindow, setWidthWindow] = useState(0)

  useEffect(() => {
    const updateWidth = () => {
      const width = document.body.clientWidth
      setWidthWindow(width)
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
  })

  const containerTabs = useRef<HTMLDivElement>(null)
  const [_tabsList, setTabsList] = useState([...tabsList])
  const [linePosition, setLinePosition] = useState({
    left: 0,
    width: 0,
  })

  const animateLine = () => {
    const activeIndex = _tabsList.findIndex((option) => !!option.isActive)
    if (activeIndex < 0) return

    const activeEl =
      containerTabs?.current?.querySelectorAll('.sb-tab')[activeIndex]

    const lineRect = activeEl?.getBoundingClientRect()
    const parentRect = containerTabs?.current?.getBoundingClientRect()

    setLinePosition({
      left: lineRect && parentRect ? lineRect?.left - parentRect?.left + 8 : 0,
      width: lineRect ? lineRect?.width - 16 : 0,
    })
  }

  const updateOptions = (index: number) => {
    const newOptions = [...tabsList].map((tab) => ({
      ...tab,
      isActive: false,
    }))

    newOptions[index].isActive = true
    setTabsList(newOptions)
    animateLine()
    !!onChange && onChange(newOptions[index])
  }

  useEffect(() => {
    animateLine()
    // eslint-disable-next-line
  }, [_tabsList, widthWindow])

  return (
    <div
      className={['sb-tabs', `sb-tabs--${color}`, isCenter, isSmall].join(' ')}
      ref={containerTabs}
      data-testid='sb-tabs'
    >
      {_tabsList &&
        _tabsList.map((tab, index) => (
          <Tab
            tab={tab}
            index={index}
            key={`sb-tab-${index}`}
            updateOptions={updateOptions}
          />
        ))}
      <div
        className={`sb-tabs--line sb-tabs--${color}`}
        style={{
          left: `${linePosition.left}px`,
          width: `${linePosition.width}px`,
        }}
      />
    </div>
  )
}

const Tab = ({ tab, updateOptions, index }: TabProps) => {
  const isActived = !!tab?.isActive ? 'sb-tab--active' : ''
  const isDisabled = !!tab?.isDisabled ? 'sb-tab--disabled' : ''

  const animationClick = (e: any) => {
    const { pageX, pageY } = e
    const { width, height } = e.target.getBoundingClientRect()

    e.target.style.setProperty('--top', `${(pageY / height) * 100}%`)
    e.target.style.setProperty('--left', `${(pageX / width) * 100}%`)

    const dx = pageX > width / 2 ? pageX : width - pageX
    const dy = pageY > height / 2 ? pageY : height - pageY
    const size = Math.sqrt(dx ** 2 + dy ** 2) * 2
    e.target.style.setProperty('--size', `${size}px`)
  }

  return (
    <div
      className={['sb-tab', isActived, isDisabled].join(' ')}
      onClick={() => {
        !tab?.isDisabled && updateOptions && updateOptions(index)
      }}
      onMouseDown={animationClick}
      data-testid='sb-tab'
    >
      <span className='sb-tab--text'>{tab.text}</span>
    </div>
  )
}

Tabs.propTypes = {
  /**
   * List of Tabs
   */
  tabsList: PropTypes.arrayOf(PropTypes.object),
  /**
   * Alignment
   */
  center: PropTypes.bool,
  /**
   * Small
   */
  small: PropTypes.bool,
  /**
   * Color of Tab
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
  /**
   * onChange
   */
  onChange: PropTypes.func,
}

Tabs.defaultProps = {
  tabsList: [
    {
      text: 'Pestaña 1',
      value: '1',
    },
    {
      text: 'Pestaña 2',
      value: '2',
      isActive: true,
    },
    {
      text: 'Pestaña 3',
      value: '3',
      isDisabled: true,
    },
    {
      text: 'Pestaña 4',
      value: '4',
    },
  ],
  center: true,
  small: false,
  color: 'primary',
  onChange: () => {},
}

export default Tabs
